import React, { useState, useEffect } from 'react';
import { formatearFecha } from '../utils';

const Diario = ({
  entradasDiario,
  editarEntradaDiario,
  eliminarEntradaDiario,
  setVistaActual,
  modalEdicionAbierto,
  setModalEdicionAbierto,
  entradaEditando,
  setEntradaEditando,
  guardarCambiosEntrada,
  handleEntradaAlimentoChange,
  handleEntradaNumeroChange,
  formatearFecha,
  isoToLocalDatetime,
  localDatetimeToIso,
  alimentosComunes = [],
  alimentosPersonalizados = []
}) => {
  // Estado para ordenación
  const [sortBy, setSortBy] = useState('fecha');
  const [sortDir, setSortDir] = useState('desc');

  // Estado para paginación
  const [entradasPorPagina, setEntradasPorPagina] = useState(25);
  const [paginaActual, setPaginaActual] = useState(1);

  // Estado para el total de HC en edición
  const [totalHcEdicion, setTotalHcEdicion] = useState(entradaEditando?.totalCarbs || 0);

  // Estado para mostrar más en la tabla
  const [showMoreEntry, setShowMoreEntry] = useState(null);

  // Actualiza el total de HC en tiempo real al editar alimentos
  useEffect(() => {
    if (modalEdicionAbierto && entradaEditando) {
      const total = entradaEditando.alimentos.reduce((sum, alimento) => {
        const cantidad = parseFloat(alimento.cantidad) || 0;
        const carbsPor100 = parseFloat(alimento.carbsPor100) || 0;
        return sum + (cantidad * carbsPor100) / 100;
      }, 0);
      setTotalHcEdicion(total);
      // También actualiza el objeto de edición para que al guardar se use el valor correcto
      setEntradaEditando(prev => prev ? { ...prev, totalCarbs: total } : prev);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalEdicionAbierto, entradaEditando?.alimentos]);

  // Función para ordenar
  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  // Ordenar entradas
  const sortedEntradas = [...entradasDiario].sort((a, b) => {
    let vA, vB;
    switch (sortBy) {
      case 'fecha':
        vA = a.fecha;
        vB = b.fecha;
        break;
      case 'alimento':
        vA = (a.alimentos[0]?.nombre || '').toLowerCase();
        vB = (b.alimentos[0]?.nombre || '').toLowerCase();
        break;
      case 'peso':
        vA = a.alimentos.reduce((sum, al) => sum + (parseFloat(al.cantidad) || 0), 0);
        vB = b.alimentos.reduce((sum, al) => sum + (parseFloat(al.cantidad) || 0), 0);
        break;
      case 'carbsPor100':
        vA = a.alimentos.reduce((sum, al) => sum + (parseFloat(al.carbsPor100) || 0), 0);
        vB = b.alimentos.reduce((sum, al) => sum + (parseFloat(al.carbsPor100) || 0), 0);
        break;
      case 'totalCarbs':
        vA = a.totalCarbs;
        vB = b.totalCarbs;
        break;
      case 'glucosa':
        vA = a.glucosa || 0;
        vB = b.glucosa || 0;
        break;
      case 'insulina':
        vA = a.insulina || 0;
        vB = b.insulina || 0;
        break;
      default:
        vA = '';
        vB = '';
    }
    if (typeof vA === 'string' && typeof vB === 'string') {
      const cmp = vA.localeCompare(vB, 'es', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    } else {
      return sortDir === 'asc' ? vA - vB : vB - vA;
    }
  });

  // Calcular paginación
  const totalPaginas = Math.ceil(sortedEntradas.length / entradasPorPagina);
  const startIdx = (paginaActual - 1) * entradasPorPagina;
  const endIdx = startIdx + entradasPorPagina;
  const entradasPagina = sortedEntradas.slice(startIdx, endIdx);

  // Cambiar página
  const handlePageChange = (nuevaPagina) => {
    setPaginaActual(nuevaPagina);
  };

  // Cambiar entradas por página
  const handleEntradasPorPagina = (e) => {
    setEntradasPorPagina(Number(e.target.value));
    setPaginaActual(1);
  };

  return (
    <>
      <div className="w-full max-w-5xl mx-auto p-6 bg-white rounded-xl lg:shadow-none shadow-none overflow-x-auto lg:overflow-x-visible flex flex-col items-center">
        <div className="mb-8 w-full">
          <h1 className="text-3xl font-bold text-center text-indigo-600">Diario</h1>
          <div className="mt-2 h-1 w-20 bg-indigo-500 mx-auto rounded-full"></div>
          <p className="mt-3 text-center text-gray-600 text-sm">Historial de tus comidas y registros</p>
        </div>
        <div className="w-full flex justify-center mb-6">
          <button
            className="bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-2 px-6 rounded-lg shadow-sm transition-colors"
            onClick={() => {
              setEntradaEditando({
                id: Date.now().toString(),
                fecha: new Date().toISOString(),
                alimentos: [{ id: Date.now(), nombre: '', cantidad: '', carbsPor100: '' }],
                totalCarbs: 0,
                glucosa: '',
                insulina: ''
              });
              setModalEdicionAbierto(true);
            }}
          >
            Añadir nueva entrada
          </button>
        </div>
        {entradasDiario.length > 0 ? (
          <>
          <div className="overflow-x-auto lg:overflow-x-visible w-full flex justify-center">
            <table className="bg-white border border-gray-200 rounded-lg lg:shadow-none shadow-none lg:w-auto mx-auto text-center" style={{ tableLayout: 'auto' }}>
              <colgroup>
                <col style={{ width: '130px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '120px' }} />
              </colgroup>
              <thead className="bg-gradient-to-r from-indigo-100 to-purple-100">
              <tr>
                <th className="py-2 px-3 text-xs font-semibold text-gray-600 cursor-pointer select-none text-center whitespace-nowrap" onClick={() => handleSort('fecha')}>
                  Fecha{sortBy === 'fecha' && (sortDir === 'asc' ? ' ▲' : ' ▼')}
                </th>
                <th className="py-2 px-3 text-xs font-semibold text-gray-600 cursor-pointer select-none text-center whitespace-nowrap" onClick={() => handleSort('alimento')}>
                  Alimento{sortBy === 'alimento' && (sortDir === 'asc' ? ' ▲' : ' ▼')}
                </th>
                <th className="py-2 px-3 text-xs align-middle text-center extra-col" onClick={() => handleSort('peso')}>Peso (g){sortBy === 'peso' && (sortDir === 'asc' ? ' ▲' : ' ▼')}</th>
                <th className="py-2 px-3 text-xs align-middle text-center extra-col" onClick={() => handleSort('carbsPor100')}>HC/100g{sortBy === 'carbsPor100' && (sortDir === 'asc' ? ' ▲' : ' ▼')}</th>
                <th className="py-2 px-3 text-xs align-middle text-center" onClick={() => handleSort('totalCarbs')}>Total HC (g){sortBy === 'totalCarbs' && (sortDir === 'asc' ? ' ▲' : ' ▼')}</th>
                <th className="py-2 px-4 text-xs align-middle text-center extra-col" style={{ minWidth: '90px' }} onClick={() => handleSort('glucosa')}>Glucosa (mg/dl){sortBy === 'glucosa' && (sortDir === 'asc' ? ' ▲' : ' ▼')}</th>
                <th className="py-2 px-3 text-xs align-middle text-center extra-col" onClick={() => handleSort('insulina')}>Insulina (U){sortBy === 'insulina' && (sortDir === 'asc' ? ' ▲' : ' ▼')}
                </th>
                <th className="py-2 px-3 text-xs align-middle text-center extra-col">Acciones</th>
              </tr>
            </thead>
              <tbody>
                {entradasPagina.map((entrada) => (
                  <tr key={entrada.id} className="border-t border-gray-100 hover:bg-indigo-50 transition">
                    <td className="py-2 px-3 text-xs">{formatearFecha(entrada.fecha)}</td>
                    <td className="py-2 px-3 text-xs align-middle text-center">
                      <ul className="list-none m-0 p-0 flex flex-col items-center justify-center h-full min-h-[24px]">
                        {entrada.alimentos.map((a, idx) => (
                          <li key={idx} style={{ whiteSpace: 'nowrap' }}>{a.nombre}</li>
                        ))}
                      </ul>
                    </td>
                    <td className="py-2 px-3 text-xs align-middle text-center extra-col">
                      <ul className="list-none m-0 p-0 flex flex-col items-center justify-center h-full min-h-[24px]">
                        {entrada.alimentos.map((a, idx) => (
                          <li key={idx}>{a.cantidad}</li>
                        ))}
                      </ul>
                    </td>
                    <td className="py-2 px-3 text-xs align-middle text-center extra-col">
                      <ul className="list-none m-0 p-0 flex flex-col items-center justify-center h-full min-h-[24px]">
                        {entrada.alimentos.map((a, idx) => (
                          <li key={idx}>{a.carbsPor100}</li>
                        ))}
                      </ul>
                    </td>
                    {/* Total HC (g) cell */}
                    <td className="py-2 px-3 text-xs align-middle text-center flex items-center justify-center gap-2">
                      <span>{entrada.totalCarbs.toFixed(1)}g</span>
                      {/* Show info button only on small screens */}
                      <button
                        onClick={() => setShowMoreEntry(entrada)}
                        className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-full p-1 ml-1 block lg:hidden"
                        aria-label="Ver detalles"
                        style={{ minWidth: 28, minHeight: 28 }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01" />
                        </svg>
                      </button>
                    </td>
                    <td className="py-2 px-4 text-xs align-middle text-center extra-col" style={{ minWidth: '90px' }}>
                      {entrada.glucosa !== null ? `${entrada.glucosa} mg/dl` : '-'}
                    </td>
                    <td className="py-2 px-3 text-xs align-middle text-center extra-col">{entrada.insulina !== null ? `${entrada.insulina} U` : '-'}</td>
                    <td className="py-2 px-3 text-xs flex gap-2 align-middle text-center extra-col">
                      <button onClick={() => editarEntradaDiario(entrada)} className="bg-yellow-400 hover:bg-yellow-500 text-white px-2 py-1 rounded text-xs">Editar</button>
                      <button onClick={() => eliminarEntradaDiario(entrada.id)} className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs">Eliminar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Show More Modal */}
          {showMoreEntry && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
              <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full m-4 relative flex flex-col items-center justify-center" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', position: 'fixed' }}>
                <button onClick={() => setShowMoreEntry(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Detalles de la entrada</h3>
                <div className="space-y-2 w-full">
                  <div><b>Fecha:</b> {formatearFecha(showMoreEntry.fecha)}</div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-200 rounded-lg text-xs text-center mt-2">
                      <thead className="bg-indigo-100">
                        <tr>
                          <th className="py-1 px-2">Alimento</th>
                          <th className="py-1 px-2">Peso (g)</th>
                          <th className="py-1 px-2">HC/100g</th>
                        </tr>
                      </thead>
                      <tbody>
                        {showMoreEntry.alimentos.map((a, idx) => (
                          <tr key={idx}>
                            <td className="py-1 px-2">{a.nombre}</td>
                            <td className="py-1 px-2">{a.cantidad}</td>
                            <td className="py-1 px-2">{a.carbsPor100}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div><b>Total HC:</b> {showMoreEntry.totalCarbs.toFixed(1)}g</div>
                  <div><b>Glucosa:</b> {showMoreEntry.glucosa !== null ? `${showMoreEntry.glucosa} mg/dl` : '-'}</div>
                  <div><b>Insulina:</b> {showMoreEntry.insulina !== null ? `${showMoreEntry.insulina} U` : '-'}</div>
                  <div className="flex gap-2 justify-center mt-4">
                    <button onClick={() => { editarEntradaDiario(showMoreEntry); setShowMoreEntry(null); }} className="bg-yellow-400 hover:bg-yellow-500 text-white px-3 py-1 rounded text-xs">Editar</button>
                    <button onClick={() => { eliminarEntradaDiario(showMoreEntry.id); setShowMoreEntry(null); }} className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs">Eliminar</button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <style>{`
            @media (max-width: 900px) {
              .extra-col, .show-more-header { display: none !important; }
              .show-more-cell { display: table-cell !important; }
            }
            @media (min-width: 901px) {
              .show-more-cell { display: none !important; }
            }
          `}</style>
          {/* Selector de paginación */}
          <div className="w-full flex justify-end items-center mt-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Entradas por página:</span>
              <select value={entradasPorPagina} onChange={handleEntradasPorPagina} className="border border-gray-300 rounded px-2 py-1 text-xs">
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <div className="flex items-center gap-1 ml-4">
                {/* Botón siguiente */}
                <button onClick={() => handlePageChange(paginaActual + 1)}
                  disabled={paginaActual === totalPaginas}
                  className={`px-2 py-1 text-xs rounded border border-gray-300 ${paginaActual === totalPaginas ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50 hover:text-indigo-900'} transition`}
                >
                  Siguiente
                </button>
              </div>
              {totalPaginas > 1 && (
                <span className="text-xs ml-2">Página {paginaActual} de {totalPaginas}</span>
              )}
            </div>
          </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="mb-4 text-gray-500">No hay entradas en tu diario todavía.</p>
            <button onClick={() => setVistaActual('calculadora')} className="bg-indigo-500 hover:bg-indigo-600 text-white py-2 px-6 rounded-lg transition-colors shadow-sm">Ir a la calculadora</button>
          </div>
        )}
        {/* Modal de edición */}
        {modalEdicionAbierto && entradaEditando && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full m-4 relative flex flex-col items-center justify-center" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', position: 'fixed' }}>
              <button onClick={() => setModalEdicionAbierto(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Editar entrada</h3>
              <div className="space-y-4 w-full">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Fecha y hora</label>
                  <input
                    type="datetime-local"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 outline-none"
                    value={entradaEditando.fecha ? isoToLocalDatetime(entradaEditando.fecha) : ''}
                    onChange={e => setEntradaEditando({ ...entradaEditando, fecha: localDatetimeToIso(e.target.value) })}
                  />
                </div>
                <div>
                  <div className="grid grid-cols-4 gap-2 mb-2 font-semibold text-xs text-gray-600 text-center">
                    <div>Alimento</div>
                    <div>Peso (g)</div>
                    <div>HC/100g</div>
                    <div>Total HC</div>
                  </div>
                  {entradaEditando.alimentos.map((alimento, idx) => {
                    // Build sorted, deduplicated food list as in Calculadora HC
                    const allFoods = [
                      ...alimentosPersonalizados,
                      ...alimentosComunes.filter(a => !alimentosPersonalizados.some(ap => ap.nombre.toLowerCase() === a.nombre.toLowerCase()))
                    ].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));
                    return (
                      <div key={alimento.id} className="grid grid-cols-4 gap-2 mb-1 items-center">
                        <select
                          className="p-2 border border-gray-200 rounded-lg text-xs"
                          value={alimento.nombre}
                          onChange={e => {
                            const nombre = e.target.value;
                            const found = allFoods.find(a => a.nombre === nombre);
                            setEntradaEditando(prev => ({
                              ...prev,
                              alimentos: prev.alimentos.map(a => a.id === alimento.id ? {
                                ...a,
                                nombre,
                                carbsPor100: found ? found.carbsPor100 : ''
                              } : a)
                            }));
                          }}
                        >
                          <option value="">Selecciona alimento</option>
                          {allFoods.map(a => (
                            <option key={a.nombre} value={a.nombre}>{a.nombre}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          className="p-2 border border-gray-200 rounded-lg text-xs"
                          value={alimento.cantidad}
                          onChange={e => handleEntradaNumeroChange(alimento.id, 'cantidad', e.target.value)}
                          placeholder="Cantidad"
                        />
                        <input
                          type="number"
                          className="p-2 border border-gray-200 rounded-lg text-xs bg-gray-100 cursor-not-allowed"
                          value={alimento.carbsPor100}
                          readOnly
                          tabIndex={-1}
                        />
                        <div className="text-xs text-center">
                          {alimento.cantidad && alimento.carbsPor100
                            ? ((parseFloat(alimento.cantidad) * parseFloat(alimento.carbsPor100) / 100).toFixed(1) + 'g')
                            : '0g'}
                        </div>
                      </div>
                    );
                  })}
                  <div className="grid grid-cols-4 gap-2 mt-2 items-center">
                    <div className="col-span-3 border-t border-gray-300"></div>
                    <div className="font-bold text-indigo-700 text-center">{totalHcEdicion.toFixed(1)}g</div>
                  </div>
                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      className="bg-indigo-500 hover:bg-indigo-600 text-white px-2 py-1 rounded text-xs flex items-center"
                      onClick={() => setEntradaEditando({
                        ...entradaEditando,
                        alimentos: [
                          ...entradaEditando.alimentos,
                          { id: Date.now(), nombre: '', cantidad: '', carbsPor100: '' }
                        ]
                      })}
                    >+ Añadir alimento</button>
                  </div>
                  <datalist id="alimentos-list">
                    {/* Aquí deberías mapear todos los alimentos disponibles */}
                  </datalist>
                </div>
                <div className="flex gap-4">
                  <div className="w-1/2">
                    <label className="text-sm font-medium text-gray-700 block mb-1">Glucosa</label>
                    <input
                      type="number"
                      className="w-full p-2 border border-gray-200 rounded-lg text-xs"
                      value={entradaEditando.glucosa || ''}
                      onChange={e => setEntradaEditando({ ...entradaEditando, glucosa: e.target.value })}
                      placeholder="mg/dl"
                    />
                  </div>
                  <div className="w-1/2">
                    <label className="text-sm font-medium text-gray-700 block mb-1">Insulina</label>
                    <input
                      type="number"
                      className="w-full p-2 border border-gray-200 rounded-lg text-xs"
                      value={entradaEditando.insulina || ''}
                      onChange={e => setEntradaEditando({ ...entradaEditando, insulina: e.target.value })}
                      placeholder="U"
                    />
                  </div>
                </div>
                <div className="pt-2">
                  <button onClick={guardarCambiosEntrada} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white py-2 px-4 rounded-lg transition-colors shadow-sm">Guardar cambios</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Diario;
